import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readStored, storageKey, writeStored } from '@/lib/storage';

/*
  useSavedViews(pageId, options) — named views of a page's filters (built-in presets + views people
  save themselves), remembered per page in this browser, with one optional default view that opens
  when the page is visited without filters in the URL. Render it with <SavedViews views={…} />
  (components/ui/saved-views) or pass it to <FilterBar views={…} />.

    const [filters, setFilters, { isDefault }] = useUrlState(FILTER_DEFAULTS);
    const views = useSavedViews('tasks', {
      presets: [
        { id: 'all', label: 'All', state: {}, count: 14 },
        { id: 'mine', label: 'My tasks', state: { person: ['me'] }, count: 3 },
      ],
      state: filters,                 // the current view state
      defaults: FILTER_DEFAULTS,      // what a missing key means (the useUrlState schema)
      pristine: isDefault,            // true when the URL carries no filters → the default view may open
      onApply: (state) => setFilters(state, { reset: true }),
    });

  The default view opens once, on mount, when `pristine` is true. applyDefaultOnLoad: false (option,
  default true) never opens it by itself, e.g. on a page that restores its filters some other way.
  Returns { views, activeId, activeView, isModified, defaultId, apply(id), save(label, { makeDefault }),
            remove(id), restore(view), setDefault(id | null) }.
  A view's `state` is partial: keys it leaves out mean "default". Storage: `sara.views.<pageId>`.
*/

const EMPTY = { views: [], defaultId: null };

function same(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = Array.isArray(a) ? a : a === undefined || a === null || a === '' ? [] : [a];
    const right = Array.isArray(b) ? b : b === undefined || b === null || b === '' ? [] : [b];
    if (left.length !== right.length) return false;
    const sortedLeft = left.map(String).sort();
    const sortedRight = right.map(String).sort();
    return sortedLeft.every((item, index) => item === sortedRight[index]);
  }
  return a === b;
}

function defaultValueOf(spec) {
  return spec && typeof spec === 'object' && !Array.isArray(spec) && typeof spec.parse === 'function' ? spec.defaultValue : spec;
}

/** Full state: `partial` over the defaults. */
export function completeViewState(partial, defaults = {}) {
  const full = {};
  Object.keys(defaults).forEach((key) => { full[key] = defaultValueOf(defaults[key]); });
  return { ...full, ...(partial || {}) };
}

export function sameViewState(a, b, defaults = {}) {
  const left = completeViewState(a, defaults);
  const right = completeViewState(b, defaults);
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => same(left[key], right[key]));
}

function readViews(pageId) {
  const stored = readStored(storageKey('views', pageId), EMPTY);
  if (!stored || !Array.isArray(stored.views)) return EMPTY;
  return {
    views: stored.views.filter((view) => view && view.id && typeof view.label === 'string'),
    defaultId: stored.defaultId || null,
  };
}

export function useSavedViews(pageId, options = {}) {
  const { presets = [], state = {}, defaults = {}, pristine = false, onApply, applyDefaultOnLoad = true } = options;
  const [stored, setStored] = useState(() => readViews(pageId));
  // The latest stored views, also for callbacks that run later (an Undo in a toast).
  const storedRef = useRef(stored);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  const persist = useCallback((next) => {
    storedRef.current = next;
    setStored(next);
    writeStored(storageKey('views', pageId), next);
  }, [pageId]);

  const views = useMemo(() => [
    ...presets.map((view) => ({ ...view, custom: false })),
    ...stored.views.map((view) => ({ ...view, custom: true })),
  ], [presets, stored.views]);

  const activeView = views.find((view) => sameViewState(view.state, state, defaults)) || null;
  const defaultId = stored.defaultId && views.some((view) => view.id === stored.defaultId) ? stored.defaultId : null;

  const apply = useCallback((id) => {
    const view = views.find((entry) => entry.id === id);
    if (view) onApplyRef.current?.(completeViewState(view.state, defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views]);

  const save = useCallback((label, { makeDefault = false } = {}) => {
    const name = String(label || '').trim();
    if (!name) return null;
    const id = `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const view = { id, label: name, state: { ...state } };
    const current = storedRef.current;
    persist({ views: [...current.views, view], defaultId: makeDefault ? id : current.defaultId });
    return view;
  }, [persist, state]);

  const remove = useCallback((id) => {
    const current = storedRef.current;
    persist({
      views: current.views.filter((view) => view.id !== id),
      defaultId: current.defaultId === id ? null : current.defaultId,
    });
  }, [persist]);

  /** Put a removed view back (Undo). */
  const restore = useCallback((view, { wasDefault = false } = {}) => {
    const current = storedRef.current;
    if (!view || current.views.some((entry) => entry.id === view.id)) return;
    const { custom, ...clean } = view;
    persist({ views: [...current.views, clean], defaultId: wasDefault ? clean.id : current.defaultId });
  }, [persist]);

  const setDefault = useCallback((id) => {
    persist({ ...storedRef.current, defaultId: id || null });
  }, [persist]);

  // Open the default view once, when the page was opened without view state in the URL.
  const appliedRef = useRef(false);
  useEffect(() => {
    if (appliedRef.current) return;
    appliedRef.current = true;
    if (!applyDefaultOnLoad || !pristine || !defaultId) return;
    const view = views.find((entry) => entry.id === defaultId);
    if (view && !sameViewState(view.state, state, defaults)) onApplyRef.current?.(completeViewState(view.state, defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    views,
    activeId: activeView ? activeView.id : null,
    activeView,
    isModified: !activeView,
    defaultId,
    apply,
    save,
    remove,
    restore,
    setDefault,
  };
}

export default useSavedViews;
